import { useEffect, useState } from "react"
import { IoAdd, IoClose } from "react-icons/io5"

import Switch from "@fuse/sharedComponents/Switch"
import {
  showErrorToast,
  showSuccessToast,
} from "@fuse/sharedComponents/themeToast/toastUtils"
import {
  EVENT_CLASS,
  EVENT_TICKET_TYPE,
  PAYMENT_TYPE,
} from "@letshang/constants"
import { get } from "lodash-es"

import { createEventWeb } from "@api/firebaseWrappedApis"
import {
  useAddTicket,
  useGetAllTickets,
} from "@api/services/events/useEventsApi"

const inputBgClass = "bg-white/20"
const inputClass =
  "w-full rounded-xl text-white p-4 placeholder:text-[#A1A1B3] focus:outline-none border-none text-base font-normal"

const TicketForm = ({
  onCreate,
  isLoading,
}: {
  onCreate: (ticketData: any) => void
  isLoading: boolean
}) => {
  const [price, setPrice] = useState(0)
  const [quantity, setQuantity] = useState(0)
  const [ticketName, setTicketName] = useState("")
  const [ticketDescription, setTicketDescription] = useState("")
  const [requireApproval, setRequireApproval] = useState(false)

  const handlePriceChange = (value: string) => {
    const sanitizedValue = value.replace(/[^0-9.]/g, "").replace(/-/g, "")

    const parts = sanitizedValue.split(".")
    if (parts.length > 2) {
      return
    }

    if (parts[1]?.length > 2) {
      return
    }

    const numValue = Number(sanitizedValue)
    if (!isNaN(numValue) && numValue >= 0) {
      setPrice(numValue)
    }
  }

  const handleQuantityChange = (value: string) => {
    const sanitizedValue = value.replace(/[^0-9]/g, "").replace(/-/g, "")

    const numValue = Number(sanitizedValue)
    if (!isNaN(numValue) && numValue >= 0) {
      setQuantity(numValue)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const isQuantityInput =
      e.currentTarget.getAttribute("data-input-type") === "quantity"

    if (e.key === "-" || e.key === "Minus") {
      e.preventDefault()
    }

    // Prevent decimal point for quantity input
    if (isQuantityInput && (e.key === "." || e.key === "Decimal")) {
      e.preventDefault()
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onCreate({
      ticketName,
      ticketDescriptions: ticketDescription,
      ticketPrice: price,
      ticketQuantity: quantity,
      requireApproval,
    })
  }

  return (
    <form className="flex flex-col gap-6 w-full" onSubmit={handleSubmit}>
      <div>
        <label className="text-white block mb-2 text-left font-medium">
          Ticket name
        </label>
        <input
          className={`${inputClass} ${inputBgClass}`}
          placeholder="e.g. VIP ticket"
          maxLength={40}
          value={ticketName}
          onChange={(e) => setTicketName(e.target.value)}
          required
        />
        <div className="text-right text-xs text-[#A1A1B3]">
          {ticketName.length}/40
        </div>
      </div>
      <div>
        <label className="text-white block mb-2 text-left font-medium">
          Ticket description
        </label>
        <textarea
          className={`${inputClass} ${inputBgClass} min-h-[100px] resize-none`}
          placeholder="e.g. You'll receive access to the VIP lounge"
          maxLength={200}
          value={ticketDescription}
          onChange={(e) => setTicketDescription(e.target.value)}
          required
        />
        <div className="text-right text-xs text-[#A1A1B3]">
          {ticketDescription.length}/200
        </div>
      </div>
      <div>
        <label className="text-white block mb-2 text-left font-medium">
          Ticket price and quantity
        </label>
        <div className="flex gap-4">
          <div className="flex-1 flex flex-col items-start gap-1">
            <div
              className={`flex items-center gap-2 ${inputBgClass} rounded-xl p-4 w-full`}
            >
              <span className="text-2xl">🎟️</span>
              <div className="flex flex-col w-full pl-3 border-l border-white/20">
                <span className="text-[#A1A1B3] text-sm text-left">Price</span>
                <div className="flex items-center gap-1">
                  {price !== 0 && <span className="text-white text-lg">$</span>}
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    className="bg-transparent text-white text-lg focus:outline-none w-full"
                    value={price === 0 ? "" : price}
                    onChange={(e) => handlePriceChange(e.target.value)}
                    onKeyDown={handleKeyDown}
                    data-input-type="price"
                    placeholder="$ 1"
                    required
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="flex-1 flex flex-col items-start gap-1">
            <div
              className={`flex items-center gap-2 ${inputBgClass} rounded-xl p-4 w-full`}
            >
              <span className="text-2xl">🛒</span>
              <div className="flex flex-col w-full pl-3 border-l border-white/20">
                <span className="text-[#A1A1B3] text-sm text-left">
                  Quantity
                </span>
                <input
                  type="number"
                  min={1}
                  step={1}
                  className="bg-transparent text-white text-lg focus:outline-none w-full"
                  value={quantity === 0 ? "" : quantity}
                  onChange={(e) => handleQuantityChange(e.target.value)}
                  onKeyDown={handleKeyDown}
                  data-input-type="quantity"
                  placeholder="1"
                  required
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-2">
        <Switch
          label="Require approval"
          checked={requireApproval}
          onChange={(e) => setRequireApproval(e.target.checked)}
          className="py-0"
        />
      </div>
      <button
        type="submit"
        disabled={isLoading}
        className={`w-full mt-4 bg-white/10 text-white rounded-full py-3 text-lg font-semibold transition ${
          isLoading ||
          !ticketName ||
          !ticketDescription ||
          price === 0 ||
          quantity === 0
            ? "opacity-50 cursor-not-allowed"
            : ""
        }`}
      >
        {isLoading ? (
          <div className="flex items-center justify-center gap-2">
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          "Create ticket"
        )}
      </button>
    </form>
  )
}

const TicketList = ({ tickets }: { tickets: any[] }) => (
  <div className="flex flex-col gap-6 w-full h-full min-h-[300px] justify-between items-center">
    <div className="flex flex-col gap-4 w-full">
      {[...tickets]
        .sort((a, b) => a.ticketPrice - b.ticketPrice)
        .map((ticket, idx) => (
          <div
            key={ticket.id || idx}
            className={`rounded-xl p-4 flex flex-col gap-2 ${inputBgClass}`}
          >
            <div className="flex justify-between items-center pb-2 border-b border-white/20">
              <div className="text-white text-left text-lg font-semibold">
                {ticket.ticketName}
              </div>
              <div className="flex items-center text-sm gap-2 text-white">
                <div className="w-2 h-2 bg-green-500 rounded-full" />
                <span>Available</span>
              </div>
            </div>
            <div className="flex flex-col mt-2 items-start">
              <div className="text-white">${ticket.ticketPrice}</div>
              <div className="text-[#A1A1B3]">
                {ticket.ticketQuantity} available
              </div>
            </div>
          </div>
        ))}
    </div>
    <div className="flex gap-2 text-white/50 text-md">
      <span className="text-xl">ℹ️</span>
      <span className="text-left text-md">
        Payments are powered by Stripe. Host is responsible for managing
        chargebacks.{" "}
        <a
          href="https://docs.stripe.com/disputes"
          className="!text-white/50 underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          Learn more
        </a>
      </span>
    </div>
  </div>
)

const TicketModal = ({
  isOpen,
  onClose,
  eventId,
}: {
  isOpen: boolean
  onClose: () => void
  eventId: string
}) => {
  const [showForm, setShowForm] = useState(false)
  const [isCreatingTicket, setIsCreatingTicket] = useState(false)
  const { mutate } = useAddTicket()
  const { data: ticketsData, refetch: refetchTickets } =
    useGetAllTickets(eventId)
  const tickets = get(ticketsData, "data.tickets", [])

  useEffect(() => {
    if (eventId) {
      refetchTickets()
    }
  }, [eventId])

  // Show form directly if no tickets exist
  useEffect(() => {
    if (tickets.length === 0) {
      setShowForm(true)
    }
  }, [tickets])

  if (!isOpen) return null

  const handleCreate = (ticketData: any) => {
    setIsCreatingTicket(true)
    mutate(
      {
        eventId,
        ticketData: {
          ticketType: EVENT_TICKET_TYPE.PAID_TICKET,
          paymentType: PAYMENT_TYPE.MANUAL,
          eventId: eventId,
          ...ticketData,
          isActive: true,
        },
      },
      {
        onSuccess: async (response) => {
          if (response?.success) {
            showSuccessToast("Ticket created successfully")
            refetchTickets()
            setShowForm(false)
          } else {
            showErrorToast("Something went wrong.")
          }
        },
        onSettled: () => {
          setIsCreatingTicket(false)
        },
      },
    )
  }

  const handleAddNew = () => setShowForm(true)

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/20"
      onClick={onClose}
      style={{ pointerEvents: "auto" }}
    >
      <div
        className="w-full md:max-w-md bg-[#252525B2] backdrop-blur-xl rounded-t-[32px] rounded-b-none p-8 pt-6 flex flex-col items-center shadow-2xl animate-slideUp relative max-h-[80%] overflow-y-auto no-scrollbar"
        style={{
          borderTopLeftRadius: 32,
          borderTopRightRadius: 32,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-16 h-2 rounded-full bg-white/20 opacity-60 mb-4 mt-1" />
        <div className="flex justify-between items-center w-full mb-6">
          <div className="flex-1" />
          <h2 className="flex-1 text-white text-2xl font-bold text-center">
            {showForm ? "New ticket" : "Tickets"}
          </h2>
          <button
            className="w-[33%] text-white text-3xl p-0 bg-transparent transition flex justify-end items-center"
            onClick={showForm ? onClose : handleAddNew}
            aria-label="Close"
            type="button"
          >
            {showForm ? (
              <IoClose />
            ) : (
              <span className="bg-white/20 rounded-full p-1">
                <IoAdd />
              </span>
            )}
          </button>
        </div>
        <div className="w-full max-w-[400px] mx-auto">
          {showForm ? (
            <TicketForm onCreate={handleCreate} isLoading={isCreatingTicket} />
          ) : (
            <TicketList tickets={tickets} />
          )}
        </div>
      </div>
      <style>{`
        .animate-slideUp {
          animation: slideUpSheet 0.35s cubic-bezier(0.4, 0, 0.2, 1);
        }
        @keyframes slideUpSheet {
          from {
            transform: translateY(100%);
            opacity: 0.7;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  )
}

export default TicketModal
